import { gql, useQuery } from "@apollo/client";
import PageHeader from "../theme/components/common/PageHeader";
import * as React from "react";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import { gettext } from "../i18n";
import { GetAllAlertHistoryQuery } from "pages/__generated__/GetAllAlertHistoryQuery";
import SessionTimeout from "components/common/SessionTimeout";
import { formatNumberLocale } from "utils/formatting";
import {
  formatNoDataPeriod,
  isStationNoDataAlarm,
  NO_DATA_ALARM_TYPE,
  NO_DATA_RECOVERED_ALARM_TYPE,
} from "utils/alarms";
import { useNavigate } from "react-router-dom";
import "./AlarmLogPage.scss";

export const GET_ALL_ALERT_HISTORY = gql`
  query GetAllAlertHistoryQuery {
    alert_history(order_by: { timestamp: desc }) {
      id
      timestamp
      exceeding_value
      email_recipients
      sms_recipients
      message_content
      alarm_type
      alert {
        id
        kind
        no_data_minutes
        lower_limit
        upper_limit
        level
        station {
          id
          identifier
          name
        }
        datasource {
          type
          source_id
          name
          unit {
            id
            symbol
          }
        }
      }
    }
  }
`;

const AlarmLogPage = () => {
  const navigate = useNavigate();
  const { data, error, loading } = useQuery<GetAllAlertHistoryQuery>(
    GET_ALL_ALERT_HISTORY
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    console.error(JSON.stringify(error, null, 2));
    return <SessionTimeout />;
  }

  const alertHistory = data?.alert_history || [];

  return (
    <div className="AlarmLogPage">
      <PageHeader title={gettext("Alarm log")} className="mb-3"></PageHeader>
      <Card className="mb-3">
        <Card.Body>
          <div className="mb-3">
            <Button variant="secondary" onClick={() => navigate("/alarms")}>
              ← Back to Alarms
            </Button>
          </div>
          <AlarmLogTable alertHistory={alertHistory} />
        </Card.Body>
      </Card>
    </div>
  );
};

interface AlarmLogTableProps {
  alertHistory: any[];
}

const AlarmLogTable = ({ alertHistory }: AlarmLogTableProps) => {
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(
    new Set()
  );

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatRecipients = (
    emailRecipients: string[] | null,
    smsRecipients: string[] | null
  ) => {
    const recipients: string[] = [];

    if (emailRecipients && emailRecipients.length > 0) {
      emailRecipients.forEach((email) => {
        recipients.push(`📧 ${email}`);
      });
    }

    if (smsRecipients && smsRecipients.length > 0) {
      smsRecipients.forEach((sms) => {
        recipients.push(`📱 ${sms}`);
      });
    }

    return recipients.length > 0 ? recipients.join(", ") : "-";
  };

  const formatAlarmType = (alert: any, history: any) => {
    if (!alert) return "-";

    if (isStationNoDataAlarm(alert)) {
      return `${gettext("No data for")}: ${formatNoDataPeriod(
        alert.no_data_minutes
      )}`;
    }

    if (history.alarm_type === "change") {
      return gettext("N/A");
    }

    const parts: string[] = [];

    if (alert.upper_limit !== null && alert.upper_limit !== undefined) {
      parts.push(
        `${gettext("Upper")}: ${formatNumberLocale(alert.upper_limit)}`
      );
    }

    if (alert.lower_limit !== null && alert.lower_limit !== undefined) {
      parts.push(
        `${gettext("Lower")}: ${formatNumberLocale(alert.lower_limit)}`
      );
    }

    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const formatParameter = (alert: any) =>
    isStationNoDataAlarm(alert)
      ? gettext("Station not sending data")
      : alert?.datasource?.name || "-";

  const formatValue = (history: any, unit: string) => {
    if (history.alarm_type === NO_DATA_ALARM_TYPE) {
      return gettext("No data");
    }
    if (history.alarm_type === NO_DATA_RECOVERED_ALARM_TYPE) {
      return gettext("Data resumed");
    }
    return history.exceeding_value !== null &&
      history.exceeding_value !== undefined
      ? `${formatNumberLocale(history.exceeding_value)} ${unit}`
      : "-";
  };

  const getLevelBadgeClass = (level: string) => {
    switch (level) {
      case "alert":
        return "badge-danger";
      case "warning":
        return "badge-warning";
      case "notice":
        return "badge-info";
      default:
        return "badge-secondary";
    }
  };

  return (
    <div className="alarm-log-table">
      <Table responsive hover>
        <thead>
          <tr>
            <th style={{ width: "40px" }}></th>
            <th>Timestamp</th>
            <th>Station</th>
            <th>Parameter</th>
            <th>Level</th>
            {/* <th>Type</th> */}
            <th>Value</th>
            <th>Alarm Limits</th>
            <th>Recipients</th>
          </tr>
        </thead>
        <tbody>
          {alertHistory.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center">
                No alarm history found
              </td>
            </tr>
          ) : (
            alertHistory.map((history) => {
              const isExpanded = expandedRows.has(history.id);
              const alert = history.alert;
              const unit = alert?.datasource?.unit?.symbol || "";

              return (
                <React.Fragment key={history.id}>
                  <tr
                    onClick={() => toggleRow(history.id)}
                    style={{ cursor: "pointer" }}
                    className={isExpanded ? "expanded-row" : ""}
                  >
                    <td className="text-center">
                      <span className="expand-icon">
                        {isExpanded ? "▽" : "▷"}
                      </span>
                    </td>
                    <td>{formatTimestamp(history.timestamp)}</td>
                    <td>{alert?.station?.name || "-"}</td>
                    <td>{formatParameter(alert)}</td>
                    <td>
                      <span
                        className={`badge ${getLevelBadgeClass(alert?.level)}`}
                      >
                        {gettext(alert?.level) || "-"}
                      </span>
                    </td>
                    {/* Uncomment to show alarm type badge */}
                    {/* <td>
                      <span
                        className={`badge ${
                          history.alarm_type === "change"
                            ? "badge-primary"
                            : "badge-secondary"
                        }`}
                      >
                        {history.alarm_type === "change"
                          ? gettext("Change")
                          : gettext("Limit")}
                      </span>
                    </td> */}
                    <td>{formatValue(history, unit)}</td>
                    <td>{formatAlarmType(alert, history)}</td>
                    <td className="recipients-cell">
                      {formatRecipients(
                        history.email_recipients,
                        history.sms_recipients
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="expanded-content">
                      <td colSpan={8}>
                        <div className="message-content">
                          <h6>Message Content:</h6>
                          <div className="message-box">
                            {history.message_content || (
                              <em>No message content available</em>
                            )}
                          </div>
                          <div className="recipients-detail mt-3">
                            <div className="row">
                              <div className="col-md-6">
                                <h6>Email Recipients:</h6>
                                {history.email_recipients &&
                                history.email_recipients.length > 0 ? (
                                  <ul>
                                    {history.email_recipients.map(
                                      (email: string, idx: number) => (
                                        <li key={idx}>{email}</li>
                                      )
                                    )}
                                  </ul>
                                ) : (
                                  <em>None</em>
                                )}
                              </div>
                              <div className="col-md-6">
                                <h6>SMS Recipients:</h6>
                                {history.sms_recipients &&
                                history.sms_recipients.length > 0 ? (
                                  <ul>
                                    {history.sms_recipients.map(
                                      (sms: string, idx: number) => (
                                        <li key={idx}>{sms}</li>
                                      )
                                    )}
                                  </ul>
                                ) : (
                                  <em>None</em>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default AlarmLogPage;
